import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
    const { login } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        const res = await login(email, password);
        setLoading(false);
        if (!res.ok) {
            setErr(res.error);
            toast.error(res.error);
            return;
        }
        toast.success("Welcome back!");
        const redirect = location.state?.from || "/my-orders";
        navigate(redirect);
    };

    return (
        <div className="min-h-[80vh] bg-slate-50 flex items-center justify-center py-16 px-4" data-testid="login-page">
            <div className="w-full max-w-md">
                <div className="bg-white border border-slate-200 rounded-sm p-10">
                    <div className="mb-8">
                        <div className="text-xs font-mono-plex uppercase tracking-[0.2em] text-blue-700 mb-3">// Sign In</div>
                        <h1 className="font-heading font-black text-3xl tracking-tighter text-slate-900">Welcome back.</h1>
                        <p className="mt-2 text-sm text-slate-600">Access your orders, reorder in seconds.</p>
                    </div>

                    <form onSubmit={submit} className="space-y-5">
                        <div>
                            <Label className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block">Email</Label>
                            <Input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="rounded-sm" data-testid="login-email-input" />
                        </div>
                        <div>
                            <Label className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block">Password</Label>
                            <Input type="password" required value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" className="rounded-sm" data-testid="login-password-input" />
                        </div>
                        {err && <div className="text-sm text-red-600" data-testid="login-error">{err}</div>}
                        <Button type="submit" disabled={loading} className="w-full h-11 rounded-sm bg-blue-700 hover:bg-blue-800 font-bold uppercase tracking-wider" data-testid="login-submit-btn">
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><LogIn className="w-4 h-4 mr-2" /> Sign In</>}
                        </Button>
                    </form>

                    <div className="mt-6 pt-6 border-t border-slate-200 text-sm text-slate-600 text-center">
                        No account? <Link to="/register" className="text-blue-700 font-bold hover:underline" data-testid="login-register-link">Create one</Link>
                    </div>
                    <div className="mt-4 text-center">
                        <Link to="/order" className="text-xs uppercase font-bold tracking-widest text-slate-500 hover:text-blue-700" data-testid="login-guest-checkout-link">
                            Continue as guest →
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
